import { useMemo } from 'react';
import { useEnrollQueue } from './EnrollQueueContext';
import { useSendGroupPipeline } from './queue-group-send';
import { useEnrollQueueOpenRequests, type OpenRequestTarget } from './useEnrollQueueOpenRequests';
import type { SendItemsOutcome } from './enroll-queue-types';

/**
 * The dock's send/status handler bundle — the object the dock shell hands to
 * whatever renders the queue rows (`PlaysPane`).
 *
 * Every member is the handler that used to be declared inline in the V1 dock
 * shell, since deleted.
 */
export interface DockSendHandlers {
  /** Send exactly one item, with its staged wire snapshot. `EnrollmentItemEditor`'s `onSend`. */
  handleSend: (itemId: number) => Promise<SendItemsOutcome>;
  /** The ONE group send/save pipeline for this dock — see `useDockSendHandlers`'s "one pipeline per dock" note. */
  handleSendGroup: (itemIds: number[]) => Promise<void>;
  /** The pipeline's surface-instance-local pending flag; OR it into `isSending` for row disables. */
  groupSendPending: boolean;
  /** Unlock the recent-send block for one item, then send it through the SAME group pipeline. */
  handleSendAnyway: (itemId: number) => Promise<void>;
  /** "Reset to base copy" — null both per-item drafts and re-materialize the sender. */
  handleResetToBase: (itemId: number, baseSender: string | null) => Promise<void>;
  /** Draft/ready toggle with the editor's current field values. */
  handleSetStatus: (
    itemId: number,
    status: 'draft' | 'ready',
    patch: { sender: string; subject_draft: string; body_draft: string },
  ) => Promise<void>;
  /** The cool-off checkbox's write. */
  handleToggleCoolOff: (itemId: number, value: boolean) => Promise<void>;
  /** `(id, contact_id)` for every queued item — what the open-request watcher matches against. */
  openRequestTargets: OpenRequestTarget[];
  /** The contact whose row an external "please open the dock" request wants expanded. */
  focusContactId: string | null;
}

/**
 * The dock's non-visual send controller, MOVED VERBATIM out of
 * the V1 `EnrollQueueSidebar.tsx` (since deleted) so the V2
 * `shell-next/rail/RailDock` runs the one copy.
 * Nothing about the behaviour changed in the move; every comment below is the
 * original's.
 *
 * **One call per dock.** `useSendGroupPipeline` keeps a per-instance
 * `pendingRef` double-click belt, so a second `useDockSendHandlers()` mounted
 * beside the first would be a second pipeline with its own idea of "pending"
 * over the same items. The shell calls this ONCE and passes the returned
 * object down (`<PlaysPane handlers={…} />`); a child never calls it.
 *
 * Deliberately NOT here (V1-only, and it stayed inline in
 * the deleted `EnrollQueueSidebar.tsx`): everything about the omnibox / search surface
 * (`searchSurface`, `closeSearchSurface`, `handleSubmitQuery`,
 * `handleSelectContact`, `handleSelectAccount`, `queueMatches`,
 * `handleSelectQueueRow`, `handleRailClick`, the tab-change surface-close
 * effect, `recentContacts`) and the V1 left-edge resize block. V2 renders no
 * omnibox at all, so threading any of it through this shared hook would be
 * dead plumbing.
 */
export function useDockSendHandlers(): DockSendHandlers {
  const {
    groups, openQueueTab, requestQueueFlash,
    patchItem, flushAutosave, resetItemToBase, sendItems, buildStagedSnapshot, saveStepOverrides,
    beginSendPipeline, endSendPipeline, runItemOp, awaitItemOps,
  } = useEnrollQueue();

  /*
   * An action elsewhere in the app asked for the dock (see
   * `lib/enroll-queue-open-request.ts`): approving a signal draft loads the queue,
   * so the rep should land on what they just queued rather than having to know the
   * dock exists.
   *
   * Subscribed here rather than in the provider because opening means "open AND
   * show the queue tab", and the tab is the dock shell's state. Idempotent: `open`
   * on an already-open dock is a no-op, and re-selecting the current tab is too.
   */
  /**
   * Approving a signal draft loads the queue, so the rep lands on the queue tab
   * with the row it just created ALREADY EXPANDED — a collapsed row hides the
   * pre-filled per-step copy, which is the whole reason to look. Flattened here
   * because the hook only needs (id, contact_id) to find the newest row.
   */
  const openRequestTargets = useMemo(
    () => groups.flatMap((group) => group.items.map((item) => ({ id: item.id, contact_id: item.contact_id }))),
    [groups],
  );
  const focusContactId = useEnrollQueueOpenRequests(openQueueTab, openRequestTargets, requestQueueFlash);

  const handleSetStatus = async (
    itemId: number,
    status: 'draft' | 'ready',
    patch: { sender: string; subject_draft: string; body_draft: string },
  ) => {
    await patchItem(itemId, { ...patch, status });
  };
  // D12 Codex round-2 fix #6 — RETURNS the admission result rather than
  // swallowing it. terra-ohe.7 (F1 U6) — also threads this item's staged wire
  // snapshot (`buildStagedSnapshot`) so the server can resolve the staged
  // trio at claim time; there is no client-side materialization to roll back
  // any more.
  const handleSend = (itemId: number) => sendItems([itemId], buildStagedSnapshot([itemId]));
  /** U13 — cool-off checkbox's write, built on the same `patchItem` every other field patches through. */
  const handleToggleCoolOff = async (itemId: number, value: boolean) => {
    await patchItem(itemId, { cool_off_override: value });
  };
  /**
   * U4 (decision 7) — "Reset to base copy": null BOTH per-item drafts
   * through the existing `patchItem`. No new endpoint; the null-sentinel
   * convention already means "no override at this rung", so the item's
   * editor reseeds from `copyPrefill` on the next mount — which, since D8,
   * is the rep's STAGED base copy over the play's campaign copy (client
   * side), and Send materializes that same value onto the item. Server-side
   * copy precedence is now just two rungs (item draft > campaign copy), so a
   * reset item with nothing staged falls through to the campaign copy exactly
   * as before.
   *
   * D12 point 6 — reset means "this row follows the play config ENTIRELY",
   * so it also re-materializes the SENDER and drops the row's
   * `senderTouched` pin. The sender can't use the copy's null-sentinel trick:
   * `sender` has no play-level rung server-side to fall through to (the queue
   * seeds the column at assignment time and `enrollment-sender.ts` reads it
   * raw), so nulling it would just re-block the row on `no_sender`. The
   * CLIENT therefore writes the effective base — `staged ?? default ?? null`,
   * computed by the card/row that owns the play — which is the same value
   * Send would have materialized anyway. Clearing the pin AFTER the PATCH
   * resolves keeps the two consistent on a rejection: a failed reset leaves
   * the row exactly as it was, badge included.
   */
  // D12 Codex ROUND-2 fixes #2 and #3 — the whole sequence moved into
  // `EnrollQueueContext.resetItemToBase`. It used to be assembled HERE out of
  // `discardAutosave` + `patchItem` + `baseCopyDraftStore.clearSenderTouched`,
  // which left two things this surface structurally cannot do:
  //
  //  - **A real write barrier (#3).** `discardAutosave` awaits whatever write
  //    it observed at call time and then deletes the entry — a drain, not a
  //    barrier. An edit made during that await was folded in and deleted with
  //    it; an edit made after the delete but before this PATCH resolved
  //    created a fresh entry whose debounce fired afterwards, re-applying
  //    exactly what Reset discarded. Barring the item's autosave for the
  //    whole window requires reaching the provider's entry machinery.
  //  - **Resetting the OPEN editor's own local state (#2).** The mounted
  //    `EnrollmentItemEditor` for this item keeps its own draft and its own
  //    WRITE/OWN provenance refs, none of which this handler could touch — so
  //    the editor kept DISPLAYING the discarded values and the rep's next
  //    keystroke re-persisted them. The context now bumps a per-item
  //    draft-reset generation once the PATCH and the refetch have both landed.
  //
  // Kept as a named wrapper rather than passing `resetItemToBase` straight
  // down: `onResetToBase`'s call sites (`PlayQueueCard`/`ContactQueueRow`'s
  // `overriddenAccessory`) are unchanged by this fix, and this is the one
  // place the dock's handler names are collected.
  const handleResetToBase = (itemId: number, baseSender: string | null) => resetItemToBase(itemId, baseSender);
  /**
   * U4 — the By Play card's "Send N", the SAME pipeline the (now-retired)
   * full-screen review dialog used to run (`useSendGroupPipeline`,
   * extracted verbatim into `queue-group-send.ts`). Instantiated here rather
   * than inside `QueueBody`: the pending flag is surface-instance-local (a
   * double-click belt for this surface's button), while the cross-surface
   * guarantee is the context's per-item lock that the pipeline claims.
   */
  const { sendGroup: handleSendGroup, pending: groupSendPending } = useSendGroupPipeline({
    // terra-ohe.7 (F1 U6) — `buildStagedSnapshot` packages the batch's staged
    // wire snapshot for `sendItems`'s POST; see `UseSendGroupPipelineArgs`.
    buildStagedSnapshot, flushAutosave, saveStepOverrides, sendItems, beginSendPipeline, endSendPipeline,
  });
  /**
   * Flow Contacts Bulk Add-to-Queue Scope 2, U3 (Decision 7) / D17
   * (flow-contacts-bulk-send QA round-3 plan, Q1 — "yeah merge anyway") —
   * "Send anyway" used to be unlock-only (PATCH `recent_invite_override`,
   * nothing else — a rep then had to click the group's/row's own Send
   * separately). D17 merges the two into the ONE click `AssignedRow`'s
   * merged Send/Send-anyway button now fires: unlock, then send this one
   * item through the SAME group pipeline "Send N"/the row's own plain Send
   * button use (`handleSendGroup`, right above) — never a second send
   * implementation.
   *
   * Both steps are sequenced through `runItemOp`/`awaitItemOps`
   * (`useItemOpQueue.ts`, exposed on the context by this fix):
   *  1. `runItemOp(itemId, …)` wraps the unlock PATCH so it is genuinely
   *     serialized against any OTHER destructive op already queued for this
   *     item (`resetItemToBase`/`assignPlay`) — the same guarantee those two
   *     already get from each other. Without this, a "Send anyway" fired
   *     mid-reassignment could PATCH the flag onto the row's OLD play, or
   *     race the reassignment's own write.
   *  2. `awaitItemOps(itemId)` afterwards is NOT redundant with step 1:
   *     `runItemOp`'s returned promise settles as soon as the PATCH itself
   *     resolves, one microtask BEFORE the queue's own bookkeeping (the
   *     depth decrement `beginSendPipeline` reads via `isItemOpPending`) has
   *     run. `handleSendGroup`'s very first synchronous statement is
   *     `beginSendPipeline`, which refuses whenever `isItemOpPending` is
   *     still true — so calling it immediately after `await
   *     runItemOp(...)` would see THIS OP's own not-yet-cleared pending flag
   *     and silently refuse to send. `awaitItemOps` genuinely drains the
   *     chain (including its cleanup) before the send pipeline claims the
   *     item — the same pattern `flushAutosave` already uses ahead of its
   *     own gated dispatch.
   *
   * A rejected PATCH throws here and the send is never attempted — the item
   * stays flagged, unchanged from the pre-merge behavior.
   *
   * KNOWN RESIDUAL GAP (Codex adversarial follow-up, D17 round) — the same
   * family as the two D12 residual concurrency gaps documented in
   * `plans/2026-09-01-004-flow-contacts-bulk-send-design-fidelity-plan.md`
   * ("send/reassignment exclusion is asymmetric"). `PlayPicker`'s `disabled`
   * prop (mounted by `AssignedRow`'s inline reassign, `disabled={readOnly}`
   * only — NOT `itemBusy`) means an ALREADY-OPEN picker stays clickable
   * through this handler's own busy window: click "Send anyway" (claims
   * `runItemOp` for the unlock PATCH), then — with the picker already
   * open from before — pick a different play before the PATCH settles.
   * That reassignment's `assignPlay` enqueues onto the SAME item's
   * `runItemOp` chain behind the unlock, so `awaitItemOps` above drains past
   * it too — but by the time `handleSendGroup` runs, `beginSendPipeline` can
   * still see a race depending on exact timing, and the failure mode is
   * SILENT (no send POST, no error surfaced) rather than a thrown error.
   * Not patched here — the standing decision after D12's 4 review rounds is
   * that this seam (per-item destructive-op vs. send-pipeline exclusion)
   * earns a real design pass, not another point patch, and this finding is
   * the SAME root cause as the already-deferred gap, not a new one. The
   * cheap mitigation already on record (disable "Change Play" while
   * `isItemBusy`) would close this too — still Spencer's call, still not
   * built.
   */
  const handleSendAnyway = async (itemId: number) => {
    await runItemOp(itemId, () => patchItem(itemId, { recent_invite_override: true }));
    await awaitItemOps(itemId);
    await handleSendGroup([itemId]);
  };

  return {
    handleSend,
    handleSendGroup,
    groupSendPending,
    handleSendAnyway,
    handleResetToBase,
    handleSetStatus,
    handleToggleCoolOff,
    openRequestTargets,
    focusContactId,
  };
}
